import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const BUSINESS_TYPES = ["ristorante", "pizzeria", "bar", "hotel", "mensa", "catering"];

export default function OnboardingPage() {
  const { checkAuth } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ org_name: "", business_type: "ristorante", seats: 50, location_name: "Sede Principale" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/onboarding", { ...form, seats: Number(form.seats) || 50 });
      await checkAuth();
      toast.success(t("common.success"));
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6" data-testid="onboarding-page">
      <div className="w-full max-w-lg bg-card border rounded-2xl shadow-sm p-6 sm:p-10 space-y-6 animate-fade-up">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="T2WORK KITCHEN" className="h-14 w-14 rounded-2xl object-contain bg-white border p-1 shadow-sm" />
          <div>
            <h1 className="font-display text-2xl font-extrabold">{t("onboardingTitle")}</h1>
            <p className="text-sm text-muted-foreground">{t("onboardingSubtitle")}</p>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-4" data-testid="onboarding-form">
          <div className="space-y-1.5">
            <Label>{t("businessName")}</Label>
            <Input data-testid="onboarding-business-name" required value={form.org_name} onChange={set("org_name")} className="h-12" placeholder="Ristorante Da Mario" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t("businessType")}</Label>
              <select data-testid="onboarding-business-type" value={form.business_type} onChange={set("business_type")}
                className="h-12 w-full rounded-md border bg-background px-3 text-sm">
                {BUSINESS_TYPES.map((b) => <option key={b} value={b}>{b[0].toUpperCase() + b.slice(1)}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("seats")}</Label>
              <Input data-testid="onboarding-seats" type="number" min={1} value={form.seats} onChange={set("seats")} className="h-12" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>{t("products.location")} — 1</Label>
            <Input data-testid="onboarding-location-name" required value={form.location_name} onChange={set("location_name")} className="h-12" />
          </div>
          <Button data-testid="onboarding-submit" type="submit" disabled={busy} className="w-full h-12 text-base font-bold tactile-btn">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : t("startNow")}
          </Button>
        </form>
      </div>
    </div>
  );
}
